/**
 * Papéis e acesso por área — v2.
 *
 * Não é "server-only" de propósito: o menu (cliente) e a guarda de rota
 * (servidor) precisam da MESMA regra. Um mapa só, dois consumidores — se
 * divergissem, o menu esconderia um item que a URL ainda abriria.
 *
 * Os papéis vêm da organização do Clerk: "org:admin" (dono da clínica) e
 * "org:member" (equipe, recepção). Papel novo criado lá cai no mais restrito
 * até ser mapeado aqui.
 */

export type Papel = "admin" | "membro";

export function paraPapel(clerkRole: string | null | undefined): Papel {
  return clerkRole === "org:admin" ? "admin" : "membro";
}

/** As áreas do app, na ordem do menu. O href casa a rota e as filhas dela. */
export const AREAS = [
  { key: "conversas", href: "/conversas", rotulo: "Conversas" },
  { key: "pacientes", href: "/pacientes", rotulo: "Pacientes" },
  { key: "agenda", href: "/agenda", rotulo: "Agenda" },
  { key: "funil", href: "/funil", rotulo: "Funil" },
  { key: "relatorio", href: "/relatorio", rotulo: "Relatório" },
  { key: "modelos", href: "/modelos", rotulo: "Modelos" },
  { key: "empresa", href: "/empresa", rotulo: "Empresa" },
  { key: "conexao", href: "/conexao", rotulo: "Conexão" },
  { key: "equipe", href: "/equipe", rotulo: "Equipe" },
  { key: "configuracoes", href: "/configuracoes", rotulo: "Configurações" },
] as const;

export type AreaKey = (typeof AREAS)[number]["key"];

/**
 * O que cada papel enxerga por padrão.
 *
 * `membro` é a recepção: atende, vê ficha, marca presença, manda modelo. Não
 * vê custo, não mexe no agente, não conecta número. O admin pode liberar
 * mais para uma pessoa específica — é o override em `membros_acesso`.
 */
const ACESSO: Record<Papel, readonly AreaKey[]> = {
  admin: AREAS.map((a) => a.key),
  membro: ["conversas", "pacientes", "agenda", "modelos", "configuracoes"],
};

/** Só o que existe em AREAS. Filtra área removida do app e lixo vindo do banco. */
export function areasValidas(bruto: unknown): AreaKey[] {
  if (!Array.isArray(bruto)) return [];
  const conhecidas = new Set<string>(AREAS.map((a) => a.key));
  return bruto.filter((x): x is AreaKey => typeof x === "string" && conhecidas.has(x));
}

/**
 * O que ESTA pessoa enxerga: o padrão do papel, ou a lista que o admin gravou.
 *
 * `null` (sem linha no banco) é o padrão do papel. Admin não é sobrescrevível
 * de propósito: um admin sem "equipe" não conseguiria consertar o próprio
 * erro — a chave trancada dentro do carro.
 */
export function areasDaPessoa(papel: Papel, override: AreaKey[] | null): readonly AreaKey[] {
  if (papel === "admin") return ACESSO.admin;
  if (!override) return ACESSO.membro;
  return override;
}

/**
 * Um caminho é permitido? Caminho que não pertence a área nenhuma (/sistema,
 * /onboarding) é liberado aqui — a guarda cobre as ÁREAS; o resto se guarda.
 */
export function podeAbrir(papel: Papel, override: AreaKey[] | null, pathname: string): boolean {
  const area = AREAS.find((a) => pathname === a.href || pathname.startsWith(a.href + "/"));
  if (!area) return true;
  return areasDaPessoa(papel, override).includes(area.key);
}

/** A tela inicial de quem entra: a primeira área que a pessoa pode ver. */
export function primeiraArea(areas: readonly AreaKey[]): string {
  const primeira = AREAS.find((a) => areas.includes(a.key));
  return primeira?.href ?? "/onboarding";
}
